<template lang="pug">
  div(
    :style="styles.wrapper"
  )
    LibraryGrid(
      fullWidth
      v-if="loaded"
    )
      .view-explanation
        h1 Users
        p Manage app user subscriptions. Search by email and assign subscriptions to users.
      .users-toolbar
        input.users-search(
          v-model="searchQuery"
          type="text"
          placeholder="Search by email..."
          @input="debouncedSearch"
        )
        button.button__action(
          @click="loadUsers"
          :disabled="loading"
        ) Refresh
        span.total-count(v-if="pagination.total > 0") {{ pagination.total }} total users
      .users-table-container
        table.users-table(v-if="users.length > 0")
          thead
            tr
              th Email
              th Person ID
              th Created
              th Current Subscription
              th Assign Subscription
          tbody
            tr(
              v-for="user in users"
              :key="user._id"
            )
              td {{ user.email }}
              td.person-id {{ user.personId || user.businessId || '—' }}
              td {{ formatDate(user.createdAt) }}
              td
                span.subscription-badge(
                  v-if="userSubscriptions[user.personId || user.businessId]"
                ) {{ userSubscriptions[user.personId || user.businessId].name }}
                span.no-subscription(
                  v-else-if="subscriptionLoadErrors[user.personId || user.businessId]"
                ) Error loading
                span.no-subscription(
                  v-else
                ) Loading...
              td.assign-cell
                select.subscription-select(
                  v-model="selectedSubscriptions[user._id]"
                )
                  option(
                    value=""
                    disabled
                  ) Select...
                  option(
                    v-for="sub in subscriptions"
                    :key="sub._id"
                    :value="sub._id"
                  ) {{ sub.name }}
                button.button__action.button__small(
                  @click="assignSubscription(user)"
                  :disabled="!selectedSubscriptions[user._id] || assigningUser === user._id"
                ) {{ assigningUser === user._id ? 'Assigning...' : 'Assign' }}
        p.no-results(v-else-if="!loading") No users found.
      .users-pagination
        button.button__action.button__small(
          @click="changePage(pagination.page - 1)"
          :disabled="pagination.page <= 1 || loading"
        ) ← Previous
        span.page-info Page {{ pagination.page }} of {{ pagination.pages || 1 }}
        button.button__action.button__small(
          @click="changePage(pagination.page + 1)"
          :disabled="pagination.page >= pagination.pages || loading"
        ) Next →
    Spinner(
      v-if="!loaded"
    )
</template>
<script>
import LibraryGrid from '../general/LibraryGrid';
import Spinner from '../general/Spinner';
import { getResource, postResource } from '../../services/Utils';
import VueNotifications from 'vue-notifications';

export default {
  name: 'userLibrary',
  components: {
    Spinner,
    LibraryGrid
  },
  notifications: {
    showSuccessMsg: {
      type: VueNotifications.types.success,
      title: '',
      icon: '✅'
    },
    showErrorMsg: {
      type: VueNotifications.types.error,
      title: '',
      icon: '❗️'
    }
  },
  data() {
    return {
      loaded: false,
      loading: false,
      users: [],
      subscriptions: [],
      userSubscriptions: {},
      subscriptionLoadErrors: {},
      selectedSubscriptions: {},
      assigningUser: null,
      searchQuery: '',
      searchTimeout: null,
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        pages: 0
      },
      styles: {
        wrapper: {
          display: 'flex',
          flexFlow: 'row nowrap',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          height: '100vh',
          width: '100%'
        }
      }
    };
  },
  methods: {
    async loadUsers() {
      this.loading = true;
      try {
        const query = `page=${this.pagination.page}&limit=${this.pagination.limit}${this.searchQuery ? `&search=${encodeURIComponent(this.searchQuery)}` : ''}`;
        const res = await getResource('/v1/app-users/list', query);
        this.users = res.users || [];
        this.pagination = res.pagination || this.pagination;

        // Load subscriptions for each user
        this.users.forEach(user => {
          const userId = user.personId || user.businessId;
          if (userId && !this.userSubscriptions[userId]) {
            this.loadUserSubscription(userId);
          }
        });
      } catch (error) {
        console.error('Error loading users:', error);
        this.showErrorMsg({ message: 'Failed to load users' });
      }
      this.loading = false;
    },

    async loadSubscriptions() {
      try {
        const res = await getResource('/v1/subscriptions/list');
        this.subscriptions = res.subscriptions || [];
      } catch (error) {
        console.error('Error loading subscriptions:', error);
      }
    },

    async loadUserSubscription(userId) {
      try {
        const res = await getResource(`/v1/app-users/${userId}/subscription`, 'env=test');
        if (res.data && res.data.success && res.data.subscription) {
          this.$set(this.userSubscriptions, userId, res.data.subscription);
        } else {
          this.$set(this.userSubscriptions, userId, { name: 'None' });
        }
      } catch (error) {
        console.error(`Error loading subscription for ${userId}:`, error);
        this.$set(this.subscriptionLoadErrors, userId, true);
        this.$set(this.userSubscriptions, userId, { name: 'Error' });
      }
    },

    async assignSubscription(user) {
      const userId = user.personId || user.businessId;
      const subscriptionId = this.selectedSubscriptions[user._id];
      if (!userId || !subscriptionId) return;

      this.assigningUser = user._id;
      try {
        await postResource(
          `/v1/app-users/${userId}/subscription`,
          'env=test',
          { subscriptionId }
        );
        this.showSuccessMsg({ message: `Subscription assigned to ${user.email}` });

        // Reload subscription for this user
        this.$set(this.userSubscriptions, userId, null);
        await this.loadUserSubscription(userId);
        this.$set(this.selectedSubscriptions, user._id, '');
      } catch (error) {
        console.error('Error assigning subscription:', error);
        this.showErrorMsg({ message: `Failed to assign subscription: ${error.error || 'Unknown error'}` });
      }
      this.assigningUser = null;
    },

    debouncedSearch() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.pagination.page = 1;
        this.loadUsers();
      }, 400);
    },

    changePage(page) {
      if (page < 1 || page > this.pagination.pages) return;
      this.pagination.page = page;
      this.loadUsers();
    },

    formatDate(dateStr) {
      if (!dateStr) return '—';
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    }
  },
  async mounted() {
    await Promise.all([
      this.loadSubscriptions(),
      this.loadUsers()
    ]);
    this.loaded = true;
  }
};
</script>
<style lang="scss" scoped>
.users-toolbar {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0 1rem;
  margin-bottom: 1rem;
  width: 100%;
}

.users-search {
  flex: 1;
  max-width: 400px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9em;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #00a8e0;
  }
}

.users-table-container {
  width: 100%;
  padding: 0 1rem;
  overflow-x: auto;
}

.users-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border: 1px solid #eee;
  font-size: 0.9em;

  th {
    background: #f5f5f5;
    padding: 10px 12px;
    text-align: left;
    font-weight: 600;
    border-bottom: 2px solid #ddd;
    white-space: nowrap;
  }

  td {
    padding: 10px 12px;
    border-bottom: 1px solid #eee;
    vertical-align: middle;
  }

  tbody tr {
    transition: background 0.15s;

    &:hover {
      background: #f9f9f9;
    }
  }
}

.person-id {
  font-family: monospace;
  font-size: 0.85em;
  color: #666;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.subscription-badge {
  background: #e8f5e9;
  color: #2e7d32;
  padding: 3px 8px;
  border-radius: 3px;
  font-size: 0.85em;
  font-weight: 500;
}

.no-subscription {
  color: #999;
  font-style: italic;
  font-size: 0.85em;
}

.assign-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}

.subscription-select {
  padding: 4px 6px;
  border: 1px solid #ddd;
  border-radius: 3px;
  font-size: 0.85em;
  min-width: 140px;
}

.button__small {
  padding: 4px 10px !important;
  font-size: 0.8em !important;
  min-width: auto !important;
}

.users-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1rem;
  width: 100%;
}

.total-count {
  color: #999;
  font-size: 0.85em;
  margin-left: auto;
}

.page-info {
  color: #666;
  font-size: 0.9em;
}

.no-results {
  color: #999;
  text-align: center;
  padding: 2rem;
  font-style: italic;
}
</style>



// WEBPACK FOOTER //
// src/components/views/UserLibrary.vue